/**
 * The Company portal's own pages (ADR-024 §4.7) — everything a portal renders
 * differently from fias.io:
 *
 *   PortalSignIn        the Continue-with-Fias hand-off to the portal
 *                       server. The sign-in page itself (`/auth/login`,
 *                       signup, forgot) is PortalSignInPage.tsx, which falls
 *                       back to this when emailed-code sign-in is unavailable.
 *                       There is never a password form on a portal.
 *   PortalSignInFailed  `/auth/sign-in-failed`: where the server sends a
 *                       sign-in that could not finish.
 *   PortalHome          `/`: the portal's front door — its landing arche, or
 *                       sign-in. (Signed-out visitors usually never load the
 *                       app here: the edge sends them to the server first.)
 *   PortalHead          title and theme colour from the portal's profile.
 *
 * The sign-in itself is server-side (backend-for-frontend): the portal
 * server keeps `state` and the PKCE verifier and redeems the code, so no
 * portal page ever handles a code or a secret.
 *
 * Every refusal reads the same neutral way: nothing here tells a visitor who
 * is a member of what.
 */
import { safeReturnTo } from './safe-return-to';
import { useEffect, useRef, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BootSplash } from '@/components/BootSplash';
import { useAuth } from '@/auth/hooks/useAuth';
import type { CompanyPortalBrand } from '@/lib/auth-api';
import { shareOrigin } from '@/lib/portal-host';
import {
  portalLandingPath,
  portalName,
  usePortalProfile,
  type PortalProfile,
} from './usePortalProfile';

/** The portal server route that starts a sign-in, returning here afterwards. */
export function portalSignInUrl(signInPath: string, returnTo: string): string {
  return `${signInPath}?${new URLSearchParams({ returnTo: safeReturnTo(returnTo) })}`;
}

/** Branded page chrome for a portal's own screens. "Secured by Fias" is always shown. */
export function PortalFrame({
  brand,
  title,
  children,
}: {
  brand: CompanyPortalBrand | null;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen flex flex-col bg-linear-to-b from-background to-muted/30">
      <div className="flex-1 flex items-center justify-center px-4 pb-20">
        <div className="w-full max-w-sm text-center">
          {brand?.logoDataUrl ? (
            <img src={brand.logoDataUrl} alt="" className="h-16 w-auto mx-auto mb-4" />
          ) : null}
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          <div className="mt-6 bg-card rounded-xl border shadow-sm p-6 space-y-4 text-left">
            {children}
          </div>
          <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            Secured by
            <img src="/fias-logo.png" alt="Fias" className="h-4 w-auto dark:invert" />
          </p>
        </div>
      </div>
    </div>
  );
}

/** Shown when the portal cannot be reached at all (held, removed, not ready). */
export function PortalUnavailable() {
  return (
    <PortalFrame brand={null} title="This portal isn't available">
      <p className="text-sm text-muted-foreground">
        Try again later, or contact the organisation that sent you here.
      </p>
    </PortalFrame>
  );
}

/**
 * Pages that belong to fias.io, not a portal: account security, connected
 * apps, Company and workspace administration, the developer and admin planes.
 * The API refuses their calls on a portal (company-host-routes.ts), so the
 * pages would only fail; a portal hands the visitor to fias.io instead.
 */
export const FIAS_ONLY_PATH_PREFIXES: readonly string[] = [
  '/auth/settings',
  '/auth/switch-user',
  '/auth/mfa-recovery-codes',
  '/oauth',
  '/company',
  '/workspaces',
  '/developer',
  '/dev-console',
  '/contributor',
  '/submissions',
  // Building arches is the contributor plane; the API refuses both on portals.
  '/a/arche_plugin_builder',
  '/a/arche_forge',
];

export function isFiasOnlyPath(pathname: string): boolean {
  return FIAS_ONLY_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** On a portal: the page at this path lives on fias.io. */
export function ManagedOnFias() {
  const location = useLocation();
  const { profile } = usePortalProfile();
  const target = `${shareOrigin()}${location.pathname}${location.search}`;
  return (
    <PortalFrame brand={profile?.brand ?? null} title="This is managed on Fias">
      <p className="text-sm text-muted-foreground">
        Account settings and administration live on fias.io, where you sign in with the same Fias
        account.
      </p>
      <Button asChild className="w-full h-11">
        <a href={target} target="_blank" rel="noopener noreferrer">
          Open on fias.io
        </a>
      </Button>
    </PortalFrame>
  );
}

/** On a portal, answers fias.io-only paths with `ManagedOnFias`; elsewhere a pass-through. */
export function PortalFiasOnlyGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  return isFiasOnlyPath(location.pathname) ? <ManagedOnFias /> : <>{children}</>;
}

/**
 * "Sign in" from a portal's own pages: to the portal's sign-in page
 * (`/auth/login`), which offers an emailed code and Continue with Fias — or,
 * where emailed codes are not set up here, hands off to fias.io itself.
 */
function SignInButton({ profile, returnTo }: { profile: PortalProfile; returnTo: string }) {
  return (
    <Button asChild className="w-full h-11">
      <Link to={`/auth/login?${new URLSearchParams({ returnTo: safeReturnTo(returnTo) })}`}>
        {profile.accessPolicy === 'open' ? 'Sign in or create an account' : 'Sign in'}
      </Link>
    </Button>
  );
}

/**
 * The fias.io hand-off, for where the portal's own sign-in cannot be offered
 * (emailed codes not set up here): straight to it, or a note while sign-in is
 * still being set up.
 */
function HandOffButton({ profile, returnTo }: { profile: PortalProfile; returnTo: string }) {
  if (!profile.signInPath) {
    return (
      <p className="text-sm text-muted-foreground">
        Sign-in for this portal is still being set up. Try again in a few minutes.
      </p>
    );
  }
  return (
    <>
      <Button asChild className="w-full h-11">
        <a href={portalSignInUrl(profile.signInPath, returnTo)}>Continue with Fias</a>
      </Button>
      <p className="text-xs text-muted-foreground">
        You&apos;ll sign in on fias.io with your Fias account, then come back here.
      </p>
    </>
  );
}

/** `/auth/login` on a portal: straight to the sign-in, back to where the user was going. */
export function PortalSignIn() {
  const { profile, isLoading, isError } = usePortalProfile();
  const location = useLocation();
  const [params] = useSearchParams();
  const returnTo =
    (location.state as { from?: string } | null)?.from ?? params.get('returnTo') ?? '/';
  const started = useRef(false);

  useEffect(() => {
    if (!profile?.signInPath || started.current) return;
    started.current = true;
    window.location.replace(portalSignInUrl(profile.signInPath, returnTo));
  }, [profile, returnTo]);

  if (isLoading || profile?.signInPath) return <BootSplash />;
  if (isError || !profile) return <PortalUnavailable />;
  // Only reached when sign-in is not set up yet.
  return (
    <PortalFrame brand={profile.brand} title={`Sign in to ${portalName(profile)}`}>
      <HandOffButton profile={profile} returnTo={returnTo} />
    </PortalFrame>
  );
}

const FAILURE_MESSAGES: Record<string, string> = {
  access_denied: "You don't have access to this portal. Contact your administrator.",
  not_ready: 'Sign-in for this portal is still being set up. Try again in a few minutes.',
  rate_limited: 'Too many sign-in attempts. Wait a few minutes and try again.',
};

/** `/auth/sign-in-failed` — the server's landing for a sign-in that could not finish. */
export function PortalSignInFailed() {
  const [params] = useSearchParams();
  const { profile } = usePortalProfile();
  const message =
    FAILURE_MESSAGES[params.get('reason') ?? ''] ??
    'This sign-in link has expired or is no longer valid. Please try again.';
  return (
    <PortalFrame brand={profile?.brand ?? null} title="Couldn't sign you in">
      <p className="text-sm text-muted-foreground">{message}</p>
      {profile && <SignInButton profile={profile} returnTo="/" />}
    </PortalFrame>
  );
}

/**
 * `/auth/signed-out` — where signing out of a portal lands. Never the
 * auto-redirecting sign-in: that goes straight to fias.io, where a Fias
 * session that is still live offers "Continue as …" — one click from the last
 * user's portal on a shared computer. This page says so instead.
 */
export function PortalSignedOut() {
  const { profile } = usePortalProfile();
  return (
    <PortalFrame brand={profile?.brand ?? null} title="You're signed out">
      <p className="text-sm text-muted-foreground">
        You&apos;ve signed out of {profile ? portalName(profile) : 'this portal'}. Your Fias account
        may still be signed in on this browser — on a shared computer, sign out of it on{' '}
        <a
          className="underline underline-offset-4 hover:text-foreground"
          href={shareOrigin()}
          target="_blank"
          rel="noopener noreferrer"
        >
          fias.io
        </a>{' '}
        too.
      </p>
      {profile && <SignInButton profile={profile} returnTo="/" />}
    </PortalFrame>
  );
}

/** `/` on a portal. */
export function PortalHome() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { profile, isLoading, isError } = usePortalProfile();
  if (isLoading || authLoading) return <BootSplash />;
  if (isError || !profile) return <PortalUnavailable />;
  // The server returns a landing only when this visitor may see it: after
  // sign-in, or before it on an open portal whose landing arche is public.
  const landing = profile.landing;
  if (isAuthenticated) {
    return (
      <Navigate to={landing ? portalLandingPath(landing) : '/a/arche_make_a_request'} replace />
    );
  }
  if (landing) return <Navigate to={portalLandingPath(landing)} replace />;
  // The portal's own sign-in page (emailed code, or the fias.io hand-off).
  return <Navigate to="/auth/login" replace />;
}

/** Title and theme colour follow the portal. Renders nothing. */
export function PortalHead() {
  const { profile } = usePortalProfile();
  useEffect(() => {
    if (!profile) return;
    document.title = portalName(profile);
    const color = profile.brand?.primaryColor;
    if (color && /^#[0-9a-fA-F]{6}$/.test(color)) {
      let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        document.head.appendChild(meta);
      }
      meta.content = color;
    }
  }, [profile]);
  return null;
}

export { safeReturnTo };
